// src/capacitor-bridge.js — expose Capacitor SDK objects on `window`.
//
// The existing project code (app.jsx, db.js, etc.) uses the global `window.*`
// pattern instead of ESM imports. To stay consistent, each Capacitor plugin
// we adopt gets surfaced here as a single `window.Cap<X>` global. Keeping
// this file focused on plugin wiring (and away from React/Supabase globals,
// which live in their own modules) makes the platform-touchpoint set easy
// to audit before App Store submission.
//
// @capacitor/app provides:
//   - addListener('appUrlOpen', ...) — fires when the OS routes a deep
//     link (vital://...) to a RUNNING app instance.
//   - getLaunchUrl() — returns the deep link the OS used to COLD-START the
//     app, or null. Required for the case where the user taps a Supabase
//     recovery email while Vital is fully terminated.

import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Haptics } from '@capacitor/haptics';

window.CapacitorApp = App;

// @capacitor/browser — opens external URLs in SFSafariViewController on
// iOS so the user stays "inside" the app (swipe-down / Done returns
// straight to the WebView with state intact). Without this, window.open
// kicks them out to standalone Safari, which on iOS is a one-way trip.
// The 'browserFinished' event fires when the in-app sheet is dismissed —
// we use it to surface the "did you finish registering?" CTA on return.
window.CapacitorBrowser = Browser;

// @capawesome/capacitor-google-sign-in — native Google Sign-In sheet.
// initialize({ clientId: webClientId, scopes }) must be called before signIn().
// signIn() returns { idToken, email, displayName, familyName, givenName, ... }.
window.CapGoogleSignIn = GoogleSignIn;

// @capacitor-community/apple-sign-in — native Sign in with Apple sheet.
// authorize({ clientId, redirectURI, scopes, nonce, state }) returns
// { response: { identityToken, authorizationCode, user, email, givenName,
// familyName } }. On web this plugin is a no-op stub; the web SIWA path
// goes through sb.auth.signInWithOAuth() instead and doesn't touch this.
window.CapacitorAppleSignIn = SignInWithApple;

// @aparajita/capacitor-biometric-auth — Face ID / Touch ID prompt.
//   BiometricAuth.checkBiometry() → { isAvailable, biometryType, reason }
//   BiometricAuth.authenticate({ reason, cancelTitle, allowDeviceCredential })
// We deliberately default `allowDeviceCredential: false` so a failed
// biometric falls through to the normal sign-in screen rather than the
// device passcode (per the requirement spec). The plugin only handles
// the prompt — it does NOT touch Keychain. Pairing it with secure-storage
// keeps the access-control layer (biometric) separate from the data
// layer (Keychain), which is the pattern Apple recommends.
window.CapacitorBiometricAuth = BiometricAuth;
window.CapacitorBiometryType = BiometryType;

// @aparajita/capacitor-secure-storage — iOS Keychain via keychain-swift.
//   SecureStorage.set(key, value)  → kSecClassGenericPassword item
//   SecureStorage.get(key)         → string | object | null
//   SecureStorage.remove(key)
//   SecureStorage.removeAll()
// We use it to persist a serialized session envelope (userId, email,
// refresh_token) so a successful biometric verify can mint a fresh
// Supabase session via refreshSession().
window.CapacitorSecureStorage = SecureStorage;

// @capacitor/preferences — non-secret key/value persistence.
//   Preferences.set({ key, value: string })
//   Preferences.get({ key })  → { value: string | null }
//   Preferences.remove({ key })
// On iOS this writes to UserDefaults — NOT Keychain. Never store
// session tokens or other secrets here; the offline course catalogue
// cache is public data and is the only thing we put in this store.
window.CapacitorPreferences = Preferences;

// @capacitor/network — connectivity status.
//   Network.getStatus()                         → { connected, connectionType }
//   Network.addListener('networkStatusChange')  → fires on flip
// Used by the offline banner. The fetch-success/failure flag in app.jsx
// is a second source of truth for cases where the OS reports "connected"
// but the API is unreachable (e.g., captive portals).
window.CapacitorNetwork = Network;

// @capacitor/push-notifications — remote APNs push.
//   PushNotifications.requestPermissions() → { receive: 'granted'|'denied' }
//   PushNotifications.register()          → triggers 'registration' event
//                                            with the APNs device token.
//   PushNotifications.addListener('registration' / 'registrationError'
//                                 / 'pushNotificationReceived'
//                                 / 'pushNotificationActionPerformed')
// We register only AFTER the user explicitly enables reminders in
// Profile → never auto-prompt at sign-in (Apple HIG + acceptance rates).
window.CapacitorPush = PushNotifications;

// @capacitor/local-notifications — used as the in-app "Send test
// reminder" fast path. Doesn't require APNs and works offline; useful
// for proving the client wiring before the Edge Function chain is set up.
window.CapacitorLocal = LocalNotifications;

// @capacitor/haptics — Taptic Engine feedback for the wheel date picker
// (<WheelDatePicker> in components.jsx). We use the SELECTION generator, which
// is exactly what a native UIPickerView emits:
//   Haptics.selectionStart()    — on drag begin
//   Haptics.selectionChanged()  — on each detent as a new row centres
//   Haptics.selectionEnd()      — on settle
// This is native-only; on web the plugin no-ops, and the picker additionally
// guards every call behind globalThis.Capacitor.isNativePlatform(). Note the
// iOS Simulator does NOT emit haptics — verify the buzz on a physical device.
window.CapacitorHaptics = Haptics;
